import { type FC, useEffect, useRef, useState, Fragment, type ReactElement } from "react";
import { Table } from "@root/shared/components/Table/legacy/Table";
import { Th } from "@root/shared/components/Table/legacy/Th";
import { Td } from "@root/shared/components/Table/legacy/Td";
import { Tr } from "@root/shared/components/Table/legacy/Tr";
import { ReadMoreButton } from "@root/shared/components/ReadMoreButton";
import * as styles from "./styles.css";

export type detailHopedConditionsProps = {
  userHopedWorkTimeRanges?: string[];
  usersHopedCities?: string[];
  usersHopedLines?: string[];
  usersHopedStationGroupCodes?: string[];
  hopedCommuterTime?: string;
  userHopedSalary?: string;
  otherHopedCondition?: string;
  usersHopedChangeWorkTime?: string;
  usersHopedCommitmentConditions?: string[];
  availableStartDateFlag?: boolean;
  availableStartDate?: string;
  availableStartDateNote?: string;
  userContactableTimeRanges?: string[];
  contactableTimeRangesNote?: string;
};

type Props = {
  detailHopedConditions: detailHopedConditionsProps;
  showReadMoreButton?: boolean;
  maxDisplayLargeTextHeight?: number;
  maxDisplaySmallTextHeight?: number;
};

// 希望条件の詳細
// ４項目以上埋まっている場合
const DETAIL_HOPED_CONDITIONS_ROW_SHOW_LENGTH = 4;

const BORDER_HEIGHT = 1;

export const DetailHopedConditionsTable: FC<Props> = ({
  detailHopedConditions: {
    userHopedWorkTimeRanges,
    usersHopedCities,
    usersHopedLines,
    usersHopedStationGroupCodes,
    hopedCommuterTime,
    userHopedSalary,
    otherHopedCondition,
    usersHopedChangeWorkTime,
    usersHopedCommitmentConditions,
    availableStartDateFlag,
    availableStartDate,
    availableStartDateNote,
    userContactableTimeRanges,
    contactableTimeRangesNote,
  },
  showReadMoreButton = false,
  maxDisplayLargeTextHeight = 0,
  maxDisplaySmallTextHeight = 0,
}) => {
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  // detailHopedConditionsから空でない値をフィルタリング
  const detailHopedConditionsValues = Object.values({
    userHopedWorkTimeRanges,
    usersHopedCities,
    usersHopedLines,
    usersHopedStationGroupCodes,
    hopedCommuterTime,
    userHopedSalary,
    otherHopedCondition,
    usersHopedChangeWorkTime,
    usersHopedCommitmentConditions,
    availableStartDate,
    availableStartDateNote,
    userContactableTimeRanges,
    contactableTimeRangesNote,
  }).filter((value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== undefined && value !== "";
  });

  const showDetailHopedConditionsReadMoreButton =
    detailHopedConditionsValues.length >= DETAIL_HOPED_CONDITIONS_ROW_SHOW_LENGTH;

  // コンテナの高さを計算
  useEffect(() => {
    if (tableRef.current) {
      const trElements = tableRef.current.rows;
      let totalHeight = 0;
      let totalBorderHeight = 0;

      for (let i = 0; i < DETAIL_HOPED_CONDITIONS_ROW_SHOW_LENGTH - 1; i += 1) {
        if (trElements[i]) {
          totalHeight += trElements[i].offsetHeight;
          if (i !== 0) {
            totalBorderHeight += BORDER_HEIGHT;
          }
        }
      }
      setContainerHeight(totalHeight + totalBorderHeight);
    }
  }, []);

  const LongTextContents = ({
    children,
    containerMaxHeight,
  }: {
    children: ReactElement;
    containerMaxHeight: number;
  }) => {
    if (!showReadMoreButton) return children;

    return (
      <ReadMoreButton size="large" containerMaxHeight={containerMaxHeight}>
        {children}
      </ReadMoreButton>
    );
  };

  const TableContent = (
    <Table elementRef={tableRef}>
      {userHopedWorkTimeRanges && userHopedWorkTimeRanges.length > 0 && (
        <Tr>
          <Th>希望勤務時間帯</Th>
          <Td>
            {userHopedWorkTimeRanges?.map((userHopedWorkTimeRange) => (
              <Fragment key={userHopedWorkTimeRange}>
                {userHopedWorkTimeRange}
                <br />
              </Fragment>
            ))}
          </Td>
        </Tr>
      )}
      {usersHopedCities && usersHopedCities.length > 0 && (
        <Tr>
          <Th>希望勤務地</Th>
          <Td>
            {usersHopedCities?.map((usersHopedCity) => (
              <Fragment key={usersHopedCity}>
                {usersHopedCity}
                <br />
              </Fragment>
            ))}
          </Td>
        </Tr>
      )}
      {usersHopedLines && usersHopedLines.length > 0 && (
        <Tr>
          <Th>希望勤務路線</Th>
          <Td>
            {usersHopedLines?.map((usersHopedLine) => (
              <Fragment key={usersHopedLine}>
                {usersHopedLine}
                <br />
              </Fragment>
            ))}
          </Td>
        </Tr>
      )}
      {usersHopedStationGroupCodes && usersHopedStationGroupCodes.length > 0 && (
        <Tr>
          <Th>希望勤務駅</Th>
          <Td>
            {usersHopedStationGroupCodes?.map((usersHopedStationGroupCode) => (
              <Fragment key={usersHopedStationGroupCode}>
                {usersHopedStationGroupCode}
                <br />
              </Fragment>
            ))}
          </Td>
        </Tr>
      )}
      {hopedCommuterTime && (
        <Tr>
          <Th>希望通勤時間</Th>
          <Td>{hopedCommuterTime}</Td>
        </Tr>
      )}
      {userHopedSalary && (
        <Tr>
          <Th>希望給与</Th>
          <Td>{userHopedSalary}</Td>
        </Tr>
      )}
      {otherHopedCondition && (
        <Tr>
          <Th>その他の希望条件</Th>
          <Td>
            {showReadMoreButton ? (
              <LongTextContents containerMaxHeight={maxDisplayLargeTextHeight}>
                <>{otherHopedCondition}</>
              </LongTextContents>
            ) : (
              otherHopedCondition
            )}
          </Td>
        </Tr>
      )}
      {usersHopedChangeWorkTime && (
        <Tr>
          <Th>
            いつまでに就職・転職
            <br />
            したいですか？
          </Th>
          <Td>{usersHopedChangeWorkTime}</Td>
        </Tr>
      )}
      {usersHopedCommitmentConditions && usersHopedCommitmentConditions.length > 0 && (
        <Tr>
          <Th>こだわりたい条件</Th>
          <Td>
            {usersHopedCommitmentConditions?.map((usersHopedCommitmentCondition) => (
              <Fragment key={usersHopedCommitmentCondition}>
                {usersHopedCommitmentCondition}
                <br />
              </Fragment>
            ))}
          </Td>
        </Tr>
      )}
      {availableStartDateFlag && (
        <Tr>
          <Th>勤務開始可能日</Th>
          <Td>
            {availableStartDate}
            {availableStartDateNote && (
              <span className={styles.informationNote}>
                {showReadMoreButton ? (
                  <LongTextContents containerMaxHeight={maxDisplaySmallTextHeight}>
                    <>{availableStartDateNote}</>
                  </LongTextContents>
                ) : (
                  availableStartDateNote
                )}
              </span>
            )}
          </Td>
        </Tr>
      )}
      {((userContactableTimeRanges && userContactableTimeRanges.length > 0) ||
        contactableTimeRangesNote) && (
        <Tr>
          <Th>連絡可能時間帯</Th>
          <Td>
            {userContactableTimeRanges && (
              <div className={styles.informationTimeRange}>
                {userContactableTimeRanges?.map((userContactableTimeRange) => (
                  <Fragment key={userContactableTimeRange}>
                    {userContactableTimeRange}
                    <br />
                  </Fragment>
                ))}
              </div>
            )}
            {contactableTimeRangesNote && (
              <span className={styles.informationNote}>{contactableTimeRangesNote}</span>
            )}
          </Td>
        </Tr>
      )}
    </Table>
  );

  return showDetailHopedConditionsReadMoreButton ? (
    <ReadMoreButton size="large" containerMaxHeight={containerHeight}>
      {TableContent}
    </ReadMoreButton>
  ) : (
    TableContent
  );
};
